/**
 * Apartment-only search picker — the same look/UX as the admin Properties
 * tab's global search (PropertyGlobalSearch: grouped "APARTMENTS n" header,
 * icon + title + `property · floor · room count` meta line, keyboard nav,
 * debounced 2+ char queries), but SELECTION-based rather than navigation-based
 * and restricted to apartments only.
 *
 * Used wherever an admin needs to pick one existing apartment to link an
 * owner to: the create-owner-account modal and the owner profile's
 * "+ Link apartment" action.
 */
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Building2, Search, SearchX, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useGlobalSearchIndex } from "@/hooks/v2/useGlobalSearchIndex";
import { useLanguage } from "@/contexts/LanguageContext";

export type PickedApartment = {
  id: number;
  label: string;
  propertyId: number;
  propertyTitle: string;
};

type Result = {
  id: number;
  title: string;
  meta: string;
  propertyId: number;
  propertyTitle: string;
};

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 200;
const MAX_RESULTS = 8;

function Highlight({ text, query }: { text: string; query: string }) {
  const q = query.trim();
  if (!q) return <>{text}</>;
  const lower = text.toLowerCase();
  const ql = q.toLowerCase();
  const nodes: React.ReactNode[] = [];
  let i = 0;
  let key = 0;
  while (i < text.length) {
    const found = lower.indexOf(ql, i);
    if (found === -1) {
      nodes.push(text.slice(i));
      break;
    }
    if (found > i) nodes.push(text.slice(i, found));
    nodes.push(
      <mark
        key={key++}
        className="rounded bg-primary/15 px-0.5 font-semibold text-foreground"
      >
        {text.slice(found, found + q.length)}
      </mark>
    );
    i = found + q.length;
  }
  return <>{nodes}</>;
}

export function ApartmentSearchPicker({
  value,
  onSelect,
  placeholder,
  inputId,
  disabled,
}: {
  value: PickedApartment | null;
  onSelect: (apartment: PickedApartment | null) => void;
  placeholder?: string;
  inputId?: string;
  disabled?: boolean;
}) {
  const { t } = useLanguage();
  const [active, setActive] = useState(false);
  const index = useGlobalSearchIndex(active);

  const listboxId = useId();
  const optionId = (id: number) => `${listboxId}-opt-${id}`;

  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const activeRowRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) setDebouncedQuery("");
    const timer = setTimeout(() => {
      if (trimmed.length >= MIN_QUERY_LENGTH) setDebouncedQuery(trimmed);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const { properties, apartments, rooms } = index;

  const roomCountByApartment = useMemo(() => {
    const m = new Map<number, number>();
    for (const r of rooms) {
      if (r.apartment_id == null) continue;
      m.set(r.apartment_id, (m.get(r.apartment_id) ?? 0) + 1);
    }
    return m;
  }, [rooms]);

  const results = useMemo<Result[]>(() => {
    const q = debouncedQuery.trim().toLowerCase();
    if (q.length < MIN_QUERY_LENGTH) return [];
    const propTitle = (id: number) => properties.get(id)?.title ?? "";
    const propAddr = (id: number) => properties.get(id)?.address ?? "";
    const inc = (s?: string | null) => !!s && s.toLowerCase().includes(q);

    return apartments
      .filter(
        (a) =>
          inc(a.label) ||
          inc(a.floor) ||
          inc(propTitle(a.property_id)) ||
          inc(propAddr(a.property_id))
      )
      .map((a) => {
        const count = roomCountByApartment.get(a.id) ?? 0;
        const meta = [
          propTitle(a.property_id) || null,
          a.floor ? `${t("admin.apartments.floor", "Floor")} ${a.floor}` : null,
          `${count} ${count === 1 ? t("admin.field.room", "room") : t("admin.field.rooms", "rooms")}`,
        ]
          .filter(Boolean)
          .join(" · ");
        return {
          id: a.id,
          title: a.label,
          meta,
          propertyId: a.property_id,
          propertyTitle: propTitle(a.property_id),
        };
      })
      .sort((a, b) => {
        const aPrefix = a.title.toLowerCase().startsWith(q) ? 0 : 1;
        const bPrefix = b.title.toLowerCase().startsWith(q) ? 0 : 1;
        if (aPrefix !== bPrefix) return aPrefix - bPrefix;
        return a.title.localeCompare(b.title);
      })
      .slice(0, MAX_RESULTS);
  }, [debouncedQuery, apartments, properties, roomCountByApartment, t]);

  const hasQuery = debouncedQuery.length >= MIN_QUERY_LENGTH;
  const showPanel = open && hasQuery && !value;
  const showSkeleton = results.length === 0 && index.isLoading;

  useEffect(() => {
    setActiveIndex(0);
  }, [debouncedQuery]);

  useEffect(() => {
    activeRowRef.current?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  useEffect(() => {
    if (!showPanel) return;
    function onPointerDown(e: PointerEvent) {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [showPanel]);

  function pick(r: Result) {
    setOpen(false);
    setQuery("");
    setDebouncedQuery("");
    onSelect({
      id: r.id,
      label: r.title,
      propertyId: r.propertyId,
      propertyTitle: r.propertyTitle,
    });
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (!showPanel || results.length === 0) {
      if (e.key === "ArrowDown" && hasQuery) setOpen(true);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const target = results[activeIndex];
      if (target) pick(target);
    }
  }

  if (value) {
    return (
      <div className="flex items-center gap-3 rounded-xl border bg-card px-3 py-2.5">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted">
          <Building2 className="h-4 w-4 text-muted-foreground" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium">{value.label}</div>
          <div className="truncate text-xs text-muted-foreground">
            {value.propertyTitle}
          </div>
        </div>
        {!disabled && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            onClick={() => onSelect(null)}
            aria-label={t("admin.action.clearSearch", "Clear search")}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          type="search"
          value={query}
          disabled={disabled}
          onChange={(e) => {
            setActive(true);
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => {
            setActive(true);
            if (hasQuery) setOpen(true);
          }}
          onKeyDown={onKeyDown}
          placeholder={
            placeholder ??
            t("admin.search.apartmentPlaceholder", "Search apartments…")
          }
          id={inputId}
          className="rounded-xl h-11 pl-9"
          role="combobox"
          aria-expanded={showPanel}
          aria-autocomplete="list"
          aria-controls={showPanel ? listboxId : undefined}
          aria-activedescendant={
            showPanel && results[activeIndex]
              ? optionId(results[activeIndex].id)
              : undefined
          }
        />
      </div>

      {showPanel && (
        <Card className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden p-0 shadow-lg">
          {showSkeleton ? (
            <div className="space-y-2 p-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3">
                  <Skeleton className="h-9 w-9 rounded-md" />
                  <div className="flex-1 space-y-1.5">
                    <Skeleton className="h-3.5 w-40" />
                    <Skeleton className="h-3 w-56" />
                  </div>
                </div>
              ))}
            </div>
          ) : results.length === 0 ? (
            <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                <SearchX className="h-5 w-5 text-muted-foreground" />
              </div>
              <p className="font-semibold">
                {t("admin.search.noMatches", "No matches for")} “
                {debouncedQuery}”
              </p>
              <p className="text-sm text-muted-foreground">
                {t(
                  "admin.search.noApartmentMatchesHint",
                  "Try an apartment name, floor, or property."
                )}
              </p>
            </div>
          ) : (
            <>
              <div
                id={listboxId}
                role="listbox"
                aria-label={t(
                  "admin.search.apartmentResultsLabel",
                  "Apartment search results"
                )}
                className="max-h-[20rem] overflow-y-auto py-1"
              >
                <div
                  aria-hidden="true"
                  className="flex items-center gap-1.5 px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                >
                  <Building2 className="h-3.5 w-3.5" />
                  {t("admin.field.apartments", "Apartments")}
                  <span className="tabular-nums opacity-70">
                    {results.length}
                  </span>
                </div>
                {results.map((r, idx) => {
                  const isActive = idx === activeIndex;
                  return (
                    <button
                      key={r.id}
                      type="button"
                      id={optionId(r.id)}
                      role="option"
                      aria-selected={isActive}
                      ref={isActive ? activeRowRef : undefined}
                      onMouseEnter={() => setActiveIndex(idx)}
                      onClick={() => pick(r)}
                      className={`flex w-full items-center gap-3 border-l-[3px] px-3 py-2 text-left transition-colors ${
                        isActive
                          ? "border-primary bg-primary/[0.09]"
                          : "border-transparent hover:bg-muted/60"
                      }`}
                    >
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium">
                          <Highlight text={r.title} query={debouncedQuery} />
                        </div>
                        <div className="truncate text-xs text-muted-foreground">
                          <Highlight text={r.meta} query={debouncedQuery} />
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center justify-between border-t bg-muted/30 px-3 py-1.5 text-[11px] text-muted-foreground">
                <span className="hidden gap-2 sm:flex">
                  <span>↑↓ {t("admin.search.navigate", "Navigate")}</span>
                  <span>↵ {t("admin.search.selectAction", "Select")}</span>
                  <span>esc {t("admin.action.close", "Close")}</span>
                </span>
                <span className="tabular-nums">
                  {results.length}{" "}
                  {results.length === 1
                    ? t("admin.search.result", "result")
                    : t("admin.search.results", "results")}
                </span>
              </div>
            </>
          )}
        </Card>
      )}
    </div>
  );
}
